import { FaCopy, FaFileExcel, FaFileCsv, FaFilePdf, FaPrint } from 'react-icons/fa';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useEffect, useRef } from 'react';

const ExportOptions = ({ data, dropdownOpen, toggleDropdown, title, headers, dataMapper }) => {
  // Create a ref for the dropdown container to detect outside clicks
  const dropdownRef = useRef(null);

  // Effect to add and remove event listeners for outside clicks
  useEffect(() => {
    // Function to handle outside clicks
    const handleOutsideClick = (event) => {
      // Close dropdown if it's open and the click is outside
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        toggleDropdown();
      }
    };

    // Add event listener when dropdown is open
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    // Cleanup function to remove event listener
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [dropdownOpen, toggleDropdown]);

  // Function to handle the copy to clipboard functionality
  const copyToClipboard = () => {
    const exportData = data.map(dataMapper).join("\n");
    const text = `${title}\n\n${headers.join("\t")}\n${exportData}`;
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Copied to clipboard!"))
      .catch(() => toast.error("Failed to copy."));
  };

  // Function to handle the export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      [title],
      [`Generated on: ${new Date().toLocaleDateString()}`],
      [],
      headers,
    ]);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
    ws['!cols'] = headers.map((header, index) => ({
      wch: index === 0 ? 5 : (header.length + 10 > 30 ? 30 : header.length + 10),
    }));
    ws['!rows'] = [{ hpt: 20 }, { hpt: 15 }, {}, {}];
    const exportData = data.length > 0
      ? data.map((item) => {
          const row = dataMapper(item).split("\t");
          const mappedRow = {};
          headers.forEach((header, idx) => {
            mappedRow[header] = row[idx] || '';
          });
          return mappedRow;
        })
      : headers.reduce((acc, header, idx) => {
          acc[header] = idx === 1 ? 'No data found' : '';
          return acc;
        }, {});
    XLSX.utils.sheet_add_json(ws, exportData, { origin: 'A5', skipHeader: true });
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);
        if (!ws[cell_ref]) continue;
        ws[cell_ref].s = ws[cell_ref].s || {};
        if (R === 0) {
          ws[cell_ref].s.alignment = { horizontal: 'center', vertical: 'center' };
        } else if (R === 3) {
          ws[cell_ref].s.alignment = { horizontal: 'left' };
        } else if (R >= 4 && C === 0) {
          ws[cell_ref].s.alignment = { horizontal: 'right' };
        }
      }
    }
    ws['!freeze'] = { xSplit: 0, ySplit: 4 };
    XLSX.utils.book_append_sheet(wb, ws, title.replace(/\s/g, ''));
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `${title.toLowerCase().replace(/\s/g, '_')}.xlsx`);
  };

  // Function to handle the export to CSV
  const exportToCSV = () => {
    const processRow = (row) => {
      return row.map(value => {
        if (value === null || value === undefined) {
          return '""'; 
        }
        
        let stringValue = value.toString();
        
        if (stringValue.includes('"')) {
          stringValue = stringValue.replace(/"/g, '""');
        }
        
        if (stringValue.search(/("|,|\n)/g) >= 0) {
          stringValue = `"${stringValue}"`;
        }
        
        return stringValue;
      }).join(',');
    };

    // 2. Prepare Headers
    const csvHeader = processRow(headers);

    // 3. Prepare Body Data
    let csvBody = "";
    if (data.length > 0) {
      csvBody = data.map(item => {
        const rawString = dataMapper(item);
        const rowArray = rawString.split("\t"); 
        return processRow(rowArray);
      }).join('\n');
    } else {
      csvBody = "No data found";
    }

    const csvContent = `${csvHeader}\n${csvBody}`;
    
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `${title.toLowerCase().replace(/\s/g, '_')}.csv`);
  };
  // Function to handle the export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(title, 14, 20);

    // 2. Add Date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
    autoTable(doc, {
      head: [headers],
      body: data.map(dataMapper).map(row => row.split("\t")),
    });
    doc.save(`${title.toLowerCase().replace(/\s/g, '_')}.pdf`);
  };

  // Function to handle the print functionality
  const handlePrint = () => {
    // Store a reference to the original window
    const originalWindow = window;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Failed to open print window. Please allow popups for this site.');
      return;
    }

    const printContent = `
      <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; font-size: 24px; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            @media print {
              table { width: 100%; max-width: 100%; }
              th, td { font-size: 10px; padding: 6px; }
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${
                data.length > 0
                  ? data.map(item => `
                      <tr>
                        ${dataMapper(item).split("\t").map(cell => `<td>${cell}</td>`).join('')}
                      </tr>
                    `).join('')
                  : `<tr><td colspan="${headers.length}" style="text-align: center;">No data found</td></tr>`
              }
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();

    // Wait for the content to load before printing
    printWindow.onload = () => {
      printWindow.print();
      printWindow.addEventListener('afterprint', () => {
        printWindow.close();
        originalWindow.focus();
      });
      setTimeout(() => {
        if (!printWindow.closed) {
          printWindow.close();
          originalWindow.focus();
        }
      }, 1000);
    };
  };

  // Function to handle the dropdown toggle
  const handleExportClick = (format) => {
    switch (format) {
      case 'Copy':
        copyToClipboard();
        break;
      case 'Excel':
        exportToExcel();
        break;
      case 'CSV':
        exportToCSV();
        break;
      case 'PDF':
        exportToPDF();
        break;
      case 'Print':
        handlePrint();
        break;
      default:
        toast.info(`Exporting as ${format} is not implemented yet.`);
    }
    toggleDropdown();
  };

  return (
    <>
      <div className="relative leading-[1px]" ref={dropdownRef}>
        <button
          onClick={toggleDropdown}
          className="text-xl text-gray-600 hover:text-gray-800 z-10"
        >
          <BsThreeDotsVertical />
        </button>
        {dropdownOpen && (
          <div className="absolute top-10 right-0 sm:left-auto mt-2 w-48 bg-white shadow-lg rounded-lg z-50 leading-5">
            <ul className="text-sm text-gray-700 rounded-md overflow-hidden">
              <button
                onClick={() => handleExportClick('Copy')}
                className="flex items-center px-4 py-2 hover:bg-gray-200 w-full text-left"
              >
                <FaCopy className="mr-2" />
                <span>Copy</span>
              </button>
              <button
                onClick={() => handleExportClick('Excel')}
                className="flex items-center px-4 py-2 hover:bg-gray-200 w-full text-left"
              >
                <FaFileExcel className="mr-2" />
                <span>Excel</span>
              </button>
              <button
                onClick={() => handleExportClick('CSV')}
                className="flex items-center px-4 py-2 hover:bg-gray-200 w-full text-left"
              >
                <FaFileCsv className="mr-2" />
                <span>CSV</span>
              </button>
              <button
                onClick={() => handleExportClick('PDF')}
                className="flex items-center px-4 py-2 hover:bg-gray-200 w-full text-left"
              >
                <FaFilePdf className="mr-2" />
                <span>PDF</span>
              </button>
              <button
                onClick={() => handleExportClick('Print')}
                className="flex items-center px-4 py-2 hover:bg-gray-200 w-full text-left"
                title='Print'
              >
                <FaPrint className="mr-2" />
                <span>Print</span>
              </button>
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default ExportOptions;
